import { ForgotUsernameAlertProps, ForgotUsernameFields } from "../../components/SmartErrorAlert";
import translate from "../translate";
import { ErrorContentGenerator, ErrorContentMap, InvalidField, InvalidFieldMessages } from "./getAlertContent";
import { apiErrorContent as forgotPassword1ApiContent } from './forgotPassword1Errors';
import { hasErrorMessage, hasInvalidField } from "../getApiErrorMessage";

export type ForgotUsernameErrState = 'tempLocked' | 'locked' | 'deactivatedAccount' | 'notFound' | 'unknown';

export const apiErrorContent: ErrorContentMap<ForgotUsernameErrState> = {
    locked: forgotPassword1ApiContent.locked,
    deactivatedAccount: forgotPassword1ApiContent.deactivatedAccount,
    tempLocked: {
        header: translate('your_account_now_locked_protect_information'),
        body: translate('you_can_try_login'),
        analyticsErr: 'Your account is now locked to protect your information. You can try to log in again in 24 hours.'
    },
    notFound: {
        // header and body empty because we show a "No account in our records page" instead of an error alert
        header: '',
        body: '',
        analyticsErr: 'No account in our records'
    },
    unknown: {
        header: translate("we_cant_find_username_at_this_time"),
        body: <>
            {translate('check_information_try_again_call')} ({<a href="tel:18006334227">1-800-633-4227</a>}).
            {' '}{translate('tty_users_can_call')} {<a href="tel:1-877-486-2048">1-877-486-2048</a>}.
        </>,
        analyticsErr: "We can't find your username at this time. Check the information you entered and try again, or call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048."
    }
};

const invalidFieldMessages: InvalidFieldMessages<ForgotUsernameFields> = {
    mbi: {
        linkText: 'medicare_number',
        messages: {
            empty: 'enter_your_medicare_number',
            invalid: 'check_mbi'
        }
    },
    lastName: {
        linkText: 'last_name',
        messages: {
            empty: 'enter_your_last_name'
        }
    },
    dateOfBirth: {
        linkText: 'date_of_birth',
        messages: {
            empty: 'enter_your_dob'
        }
    }
};

export const forgotUsernameErrFns = new ErrorContentGenerator<ForgotUsernameAlertProps, ForgotUsernameFields, ForgotUsernameErrState>({
    apiErrorContent,
    invalidFieldMessages,
    invalidFieldHeader: 'complete_items_to_get_username',
    getRefMap: props => {
        return {
            mbi: props.mbiRef,
            lastName: props.lastNameRef,
            dateOfBirth: props.dateOfBirthRef
        }
    },
    getErrState: apiResponse => {
        if (!apiResponse || apiResponse.status === 200) {
            return null;
        }

        switch (apiResponse.data.code) {
            case 400:
                const invalidFields = forgotUsernameErrFns.getApiInvalidFields(apiResponse);
                if (invalidFields.length) {
                    return null;
                }

                const { errors } = apiResponse.data;
                if (!errors) {
                    return 'unknown';
                }
                if (hasErrorMessage(errors, 'user is disabled')) {
                    return 'deactivatedAccount';
                }
                if (hasErrorMessage(errors, 'locked')) {
                    return 'locked';
                }
                return 'unknown';
            case 403:
                return 'tempLocked';
            case 404:
                return 'notFound';
            default:
                return 'unknown';
        }
    },
    getApiInvalidFields: apiResponse => {
        if (!apiResponse || apiResponse.data.code !== 400) {
            return [];
        }

        const invalidFields: InvalidField<ForgotUsernameFields>[] = [];
        const { errors } = apiResponse.data;
        if (hasInvalidField(errors, 'mbi')) {
            invalidFields.push({ name: 'mbi', type: 'invalid' });
        }
        if (hasInvalidField(errors, 'lastName')) {
            invalidFields.push({ name: 'lastName', type: 'invalid' });
        }
        if (hasInvalidField(errors, 'dateOfBirth')) {
            invalidFields.push({ name: 'dateOfBirth', type: 'invalid' });
        }
        return invalidFields;
    },
    isIntermediateStep: false
});


export const forgotUsernameAndPasswordErrFns = new ErrorContentGenerator<ForgotUsernameAlertProps, ForgotUsernameFields, ForgotUsernameErrState>({
    apiErrorContent: {
        ...apiErrorContent,
        unknown: {
            header: translate('cant_recover_username_password'),
            body: apiErrorContent.unknown.body,
            analyticsErr: "We can't recover your username and password at this time. Check the information you entered and try again, or call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048."
        }
    },
    invalidFieldMessages,
    invalidFieldHeader: 'complete_items_to_get_username',
    getRefMap: forgotUsernameErrFns.getRefMap,
    getErrState: forgotUsernameErrFns.getErrState,
    getApiInvalidFields: forgotUsernameErrFns.getApiInvalidFields,
    isIntermediateStep: true
});